import { configureStore, combineReducers } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';

import { authApi } from './api/authApi';
import { marketplaceApi } from './api/marketplaceApi';
import { categoriesApi } from './api/categoriesApi';
import { consultantTasksApi } from './api/consultantTasksApi';
import { contractsApi } from './api/contractsApi';
import { crmApi } from './api/crmApi';
import { customersApi } from './api/customersApi';
import { financeApi } from './api/financeApi';
import { observabilityApi } from './api/observabilityApi';
import { projectsApi } from './api/projectsApi';
import { quotesApi } from './api/quotesApi';
import { ratingsApi } from './api/ratingsApi';
import { servicesApi } from './api/servicesApi';
import { slackApi } from './api/slackApi';
import resetApis from './middleware/resetApis';
import authReducer from './slices/authSlice';
import { logout, setToken, setUser } from './slices/authSlice';
import chatReducer from './slices/chatSlice';
import offlineReducer from './slices/offlineSlice';
import tenantReducer from './slices/tenantSlice';

const appReducer = combineReducers({
  auth: authReducer,
  tenant: tenantReducer,
  offline: offlineReducer,
  chat: chatReducer, 
  [authApi.reducerPath]: authApi.reducer,
  [servicesApi.reducerPath]: servicesApi.reducer,
  [quotesApi.reducerPath]: quotesApi.reducer,
  [projectsApi.reducerPath]: projectsApi.reducer,
  [customersApi.reducerPath]: customersApi.reducer,
  [observabilityApi.reducerPath]: observabilityApi.reducer,
  [financeApi.reducerPath]: financeApi.reducer,
  [contractsApi.reducerPath]: contractsApi.reducer,
  [crmApi.reducerPath]: crmApi.reducer,
  [slackApi.reducerPath]: slackApi.reducer,
  [consultantTasksApi.reducerPath]: consultantTasksApi.reducer,
  [marketplaceApi.reducerPath]: marketplaceApi.reducer,
  [categoriesApi.reducerPath]: categoriesApi.reducer,
  [ratingsApi.reducerPath]: ratingsApi.reducer,
});

const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: any) => {
if (logout.match(action)) {  // Remove login/register matchers
    state = undefined;
  }
  return appReducer(state, action);
};

// 🔁 Back-compat for legacy store/store.ts action creators.
// - `auth/setAuth` with { token, user } -> dispatch setToken/setUser
// - `auth/clearAuth` -> dispatch logout()
const compatAuthMiddleware: Middleware = (api) => (next) => (action: any) => {
  const t = action?.type;
  if (t === 'auth/setAuth' || t === '__compat/auth/setAuth') {
    const { token, user } = action?.payload ?? {};
    if (typeof token !== 'undefined') api.dispatch(setToken(token));
    if (typeof user  !== 'undefined') api.dispatch(setUser(user));
    return; // swallow the legacy action
  }
  if (t === 'auth/clearAuth' || t === '__compat/auth/clearAuth') {
    api.dispatch(logout());
    return; // swallow the legacy action
  }
  return next(action);
};


export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      compatAuthMiddleware, // must run before RTK Query & reset middleware
      resetApis,
      authApi.middleware,
      servicesApi.middleware,
      quotesApi.middleware,
      projectsApi.middleware,
      customersApi.middleware,
      observabilityApi.middleware,
      financeApi.middleware,
      contractsApi.middleware,
      crmApi.middleware,
      slackApi.middleware,
      consultantTasksApi.middleware,
      marketplaceApi.middleware,
      categoriesApi.middleware,
      ratingsApi.middleware
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// FAANG-GRADE: Export rootReducer for test infrastructure
export { rootReducer };
